import { APIDetail, ARMApplicationDetail, ARMHybridDetail, ARMServer, Asset, RTFApplication } from '../../remote-api/models'
import { ResourceSelectorState } from './reducer'
import { ValueProps as ResourceSelectorProps } from '@mulesoft/monitoring-center-components/lib/components/Selector/ResourceSelector/ResourceSelector'
import { isHybrid } from '../../components/BuiltInDashboard/common/hybrid'
import {
  CH2_TARGETS,
  getResourceTypeFromTargetType,
  isTargetResource,
  Resource as ComponentsResource,
  ResourceType as ComponentsResourceType
} from '@mulesoft/monitoring-center-components/lib/models/resource'
import { RTFApplication as ComponentsRTFApplication } from '@mulesoft/monitoring-center-components/lib/models/resource/rtf'
import { APIDetail as ComponentAPIDetail, ManagedAPI } from '@mulesoft/monitoring-center-components/lib/models/api-manager'
import { CloudHubApplication } from '@mulesoft/monitoring-center-components/lib/models/resource/cloudhub'
import { HybridApplication, TargetType } from '@mulesoft/monitoring-center-components/lib/models/resource/hybrid'
import { MCVTarget } from '@mulesoft/monitoring-center-components/lib/models/resource/target'

export const RESOURCE_RESOURCE_NAME = 'resource'
export const API_RESOURCE_NAME = 'api'
export const MANAGED_API_RESOURCE_NAME = 'mapi'
export const CH_RESOURCE_NAME = 'cloudhub'
export const HYBRID_RESOURCE_NAME = 'hybrid'
export const RTF_RESOURCE_NAME = 'rtf'
export const TARGET_RESOURCE_NAME = 'target'
export const RESOURCE_PREFIX = 'MONITORING'
export const ENDPOINT_STATUS = {
  active: 'active',
  inactive: 'inactive',
  unregistered: 'unregistered'
}
export const inactivityThreshold = 300

export type ResourceSelectorProps = ResourceSelectorProps
export type ApplicationResource = ARMApplicationDetail | ARMHybridDetail | RTFApplication
export type Resource = ApplicationResource | ARMServer | Asset | ManagedAPI
export type DetailedResource = ApplicationResource | ARMServer | APIDetail | ManagedAPI

export const enum ResourceType {
  APP = 'app',
  MC = 'mc',
  SERVER = 'server',
  SERVER_GROUP = 'server_group',
  CLUSTER = 'cluster',
  API = 'api',
  HYBRID = 'hybrid',
  MANAGED_API = 'mapi'
}

export const enum AlertResourceDeploymentType {
  HYBRID = 'HYBRID',
  CLOUDHUB = 'CLOUDHUB',
  RTF = 'RTF'
}

export const isARMApplicationDetail = (resource: Resource | ApplicationResource | DetailedResource): resource is ARMApplicationDetail => {
  return !!(resource as ARMApplicationDetail).domain
}

export const isARMHybridDetail = (resource: Resource | ApplicationResource | DetailedResource): resource is ARMHybridDetail => {
  const target = (resource as ARMHybridDetail).target
  if (!target) return false

  const targetType = target.type
  if (!targetType) return false

  return isHybrid(targetType.toLowerCase())
}

export const isCloudHub2Application = (resource: Resource | ApplicationResource | DetailedResource): resource is RTFApplication => {
  const target = (resource as RTFApplication).target
  if (!target) return false

  const subtype = target.subtype
  if (!subtype) return false

  return CH2_TARGETS.indexOf(subtype) !== -1
}

export const isRTFApplication = (resource: Resource | ApplicationResource | DetailedResource): resource is RTFApplication => {
  const target = (resource as RTFApplication).target
  if (!target) return false

  return !!(target.provider)
}

export const isTarget = (resource: Resource | DetailedResource): resource is ARMServer => {
  return !!(resource as ARMServer).deployments
}

export const isAsset = (resource: Resource): resource is Asset => {
  return !!(resource as Asset).assetId
}

export const isManagedAPI = (resource: Resource | DetailedResource): resource is ManagedAPI => {
  return !!(resource as ManagedAPI).type && (resource as ManagedAPI).type === ComponentsResourceType.MANAGED_API
}

export const isApplicationResource = (resource: Resource | DetailedResource): resource is ApplicationResource => {
  return isARMApplicationDetail(resource) || isCloudHub2Application(resource) || isARMHybridDetail(resource) || isRTFApplication(resource)
}

export const isAPIDetail = (resource: DetailedResource): resource is APIDetail => {
  return !!(resource as APIDetail).masterOrganizationId
}

export const getAPIFromAPIInstance = (resources: Resource[], versionId: string): Resource | undefined => {
  const assets = resources.filter(isAsset)
  return assets.find(asset => !!asset.apis.find(api => api.id.toString() === versionId))
}

export const getResourceByIdOrInfluxId = (resources: Resource[], resourceId: string, resourceType: ComponentsResourceType, targetId?: string)
  : Resource | undefined => {
  return getResourceById(resources, resourceId, resourceType, targetId) ||
    getResourceByInfluxResourceId(resources, resourceId, resourceType, targetId)
}

export const getResourceByInfluxResourceId = (resources: Resource[], resourceId: string, resourceType: ComponentsResourceType, targetId?: string)
  : Resource | undefined => {
  return resources.find((resource: Resource) => areInfluxResourceIdAndTargetIdFromResource(resource, resourceId, resourceType, targetId))
}

export const getResourceById = (resources: Resource[], resourceId: string, resourceType: ComponentsResourceType, targetId?: string)
  : Resource | undefined => {
  return resources.find((resource: Resource) => {
    if (isARMApplicationDetail(resource) && resourceType === ComponentsResourceType.CLOUDHUB_APPLICATION)
      return getResourceId(resource) === resourceId
    if (isCloudHub2Application(resource) && resourceType === ComponentsResourceType.CLOUDHUB2_APPLICATION)
      return getResourceId(resource) === resourceId
    if (isTarget(resource) && isTargetResource(resourceType))
      return getResourceId(resource) === resourceId
    if (isAsset(resource) && resourceType === ComponentsResourceType.API)
      return getResourceId(resource) === resourceId
    if (isManagedAPI(resource) && resourceType === ComponentsResourceType.MANAGED_API)
      return getResourceId(resource) === resourceId
    if (isRTFApplication(resource) && resourceType === ComponentsResourceType.RTF_APPLICATION)
      return getResourceId(resource) === resourceId
    if (isARMHybridDetail(resource) && resourceType === ComponentsResourceType.HYBRID_APPLICATION)
      return getResourceId(resource) === resourceId
    return false
  })
}

export const areInfluxResourceIdAndTargetIdFromResource = (resource: Resource,
                                                           influxResourceId: string,
                                                           resourceType: ComponentsResourceType,
                                                           targetId?: string): boolean => {
  if (isARMApplicationDetail(resource) && resourceType === ComponentsResourceType.CLOUDHUB_APPLICATION)
    return getResourceId(resource) === influxResourceId
  if (isCloudHub2Application(resource) && resourceType === ComponentsResourceType.CLOUDHUB2_APPLICATION)
    return getResourceId(resource) === influxResourceId
  if (isTarget(resource) && isTargetResource(resourceType))
    return getResourceId(resource) === influxResourceId
  if (isAsset(resource) && resourceType === ComponentsResourceType.API)
    return getResourceId(resource) === influxResourceId
  if (isManagedAPI(resource) && resourceType === ComponentsResourceType.MANAGED_API)
    return getResourceId(resource) === influxResourceId
  if (isRTFApplication(resource) && resourceType === ComponentsResourceType.RTF_APPLICATION)
    return getResourceId(resource) === influxResourceId || (getResourceName(resource) === influxResourceId && getClusterId(resource) === targetId)
  if (isARMHybridDetail(resource) && resourceType === ComponentsResourceType.HYBRID_APPLICATION)
    return getResourceName(resource) === influxResourceId && resource.target.id.toString() === targetId
  return false
}

export const getInfluxResourceId = (resource: Resource): string => {
  if (isARMApplicationDetail(resource)) return getResourceId(resource)
  if (isCloudHub2Application(resource)) return getResourceName(resource)
  if (isAsset(resource)) return getResourceId(resource)
  if (isTarget(resource)) return getResourceId(resource)
  if (isManagedAPI(resource)) return getResourceId(resource)
  if (isRTFApplication(resource)) return getResourceName(resource)
  if (isARMHybridDetail(resource)) return getResourceName(resource)
  return ''
}

export const getResourceId = (resource: Resource): string => {
  if (isARMApplicationDetail(resource)) return resource.fullDomain
  if (isCloudHub2Application(resource)) return resource.id
  if (isAsset(resource)) return resource.id.toString()
  if (isTarget(resource)) return resource.id.toString()
  if (isManagedAPI(resource)) return resource.id.toString()
  if (isRTFApplication(resource)) return resource.id
  if (isARMHybridDetail(resource)) return resource.id.toString()
  return ''
}

export const getResourceName = (resource: Resource): string => {
  if (isARMApplicationDetail(resource)) return resource.domain
  if (isAsset(resource)) return resource.exchangeAssetName
  if (isTarget(resource)) return resource.name
  if (isManagedAPI(resource)) return resource.exchangeAssetName || resource.id.toString()
  if (isRTFApplication(resource)) return resource.name || (resource.artifact ? resource.artifact.name : resource.id)
  if (isARMHybridDetail(resource)) return resource.name
  return ''
}

export const getResourceType = (resource: Resource): ResourceType => {
  if (isARMApplicationDetail(resource)) return ResourceType.APP
  if (isAsset(resource)) return ResourceType.API
  if (isTarget(resource)) return resource.type.toLowerCase() as ResourceType
  if (isManagedAPI(resource)) return ResourceType.MANAGED_API
  if (isRTFApplication(resource)) return ResourceType.MC
  if (isARMHybridDetail(resource)) return ResourceType.HYBRID
  return ResourceType.APP
}

export const getComponentsResourceTypeFromResource = (resource: Resource): ComponentsResourceType => {
  if (isARMApplicationDetail(resource)) return ComponentsResourceType.CLOUDHUB_APPLICATION
  if (isCloudHub2Application(resource)) return ComponentsResourceType.CLOUDHUB2_APPLICATION
  if (isAsset(resource)) return ComponentsResourceType.API
  if (isTarget(resource)) return getResourceTypeFromTargetType(resource.type)
  if (isManagedAPI(resource)) return ComponentsResourceType.MANAGED_API
  if (isRTFApplication(resource)) return ComponentsResourceType.RTF_APPLICATION
  if (isARMHybridDetail(resource)) return ComponentsResourceType.HYBRID_APPLICATION
  return ComponentsResourceType.CLOUDHUB_APPLICATION
}

export const getComponentsResourceType = (resourceType: string): ComponentsResourceType => {
  if (resourceType === ResourceType.APP) return ComponentsResourceType.CLOUDHUB_APPLICATION
  if (resourceType === ResourceType.API) return ComponentsResourceType.API
  if (resourceType === ResourceType.MANAGED_API) return ComponentsResourceType.MANAGED_API
  if (resourceType === ResourceType.MC) return ComponentsResourceType.RTF_APPLICATION
  if (resourceType === ResourceType.HYBRID) return ComponentsResourceType.HYBRID_APPLICATION
  if (resourceType === ResourceType.SERVER) return ComponentsResourceType.SERVER
  if (resourceType === ResourceType.SERVER_GROUP) return ComponentsResourceType.SERVER_GROUP
  if (resourceType === ResourceType.CLUSTER) return ComponentsResourceType.CLUSTER
  return ComponentsResourceType.CLOUDHUB_APPLICATION
}

export const getAlertResourceDeploymentType = (resource: Resource): AlertResourceDeploymentType => {
  if (isARMApplicationDetail(resource)) return AlertResourceDeploymentType.CLOUDHUB
  if (isRTFApplication(resource)) return AlertResourceDeploymentType.RTF
  if (isARMHybridDetail(resource) || isTarget(resource)) return AlertResourceDeploymentType.HYBRID
  return AlertResourceDeploymentType.CLOUDHUB
}

export const getClusterId = (resource: Resource): string => {
  if (isARMApplicationDetail(resource)) return resource.versionId
  if (isRTFApplication(resource)) return resource.target.targetId || ''
  if (isARMHybridDetail(resource)) return resource.target.id.toString()
  return ''
}

export const getResourceMuleVersion = (resource?: Resource | ARMApplicationDetail | ARMHybridDetail): string | undefined => {
  if (!resource) return undefined
  if (isARMApplicationDetail(resource)) return resource.muleVersion.version
  if (isAsset(resource) || isManagedAPI(resource)) return undefined
  if (isRTFApplication(resource)) return resource.target.deploymentSettings ? resource.target.deploymentSettings.runtimeVersion : undefined
  if (isARMHybridDetail(resource)) return resource.target.muleVersion
  if (isTarget(resource)) {
    const runtimeVersion = resource.details.runtimeVersion ? resource.details.runtimeVersion : undefined
    const childServer = resource.details.servers ? resource.details.servers[0] : undefined
    return runtimeVersion || childServer && childServer.details.runtimeVersion
  }
  return undefined
}

export const getMonitoringComponentsResourceType = (resource: Resource): ComponentsResourceType => {
  if (isARMApplicationDetail(resource)) return ComponentsResourceType.CLOUDHUB_APPLICATION
  if (isCloudHub2Application(resource)) return ComponentsResourceType.CLOUDHUB2_APPLICATION
  if (isAsset(resource)) return ComponentsResourceType.API
  if (isTarget(resource)) return getResourceTypeFromTargetType(resource.type)
  if (isManagedAPI(resource)) return ComponentsResourceType.MANAGED_API
  if (isRTFApplication(resource)) return ComponentsResourceType.RTF_APPLICATION
  if (isARMHybridDetail(resource)) return ComponentsResourceType.HYBRID_APPLICATION
  return ComponentsResourceType.CLOUDHUB_APPLICATION
}

export const getTargetType = (resource: Resource): TargetType | undefined => {
  if (isARMApplicationDetail(resource)) return TargetType.CLOUDHUB
  if (isRTFApplication(resource)) return TargetType.RTF
  if (isARMHybridDetail(resource)) return resource.target.type as TargetType
  if (isAsset(resource)) return resource.targetType
  if (isTarget(resource)) return resource.type as TargetType
  if (isManagedAPI(resource)) return TargetType.CLOUDHUB
  return undefined
}

export const getTargetName = (resource: Resource): string | undefined => {
  if (isARMApplicationDetail(resource)) return undefined
  if (isRTFApplication(resource)) return resource.target.name
  if (isARMHybridDetail(resource)) return resource.target.name
  if (isAsset(resource)) return resource.targetName
  if (isManagedAPI(resource)) return undefined
  if (isTarget(resource)) return undefined
  return undefined
}

export const transformResourceToResourceSelector = (resource: Resource) => {
  return {
    id: getResourceId(resource),
    name: getResourceName(resource),
    resourceType: getMonitoringComponentsResourceType(resource),
    targetType: getTargetType(resource),
    targetName: getTargetName(resource),
    targetId: getClusterId(resource)
  }
}

export const transformToResourceSelectorProps =
  (resourceSelector: ResourceSelectorState, maxLength: number, testId?: string, disabled?: boolean): ResourceSelectorProps => {
    const selected = resourceSelector.selected && transformResourceToResourceSelector(resourceSelector.selected)
    const resources = resourceSelector.resources && resourceSelector.resources.map(resource => transformResourceToResourceSelector(resource))

    return {
      resources: resources,
      isLoading: resourceSelector.isFetching,
      selected: selected,
      maxLength: maxLength,
      dropdownHint: resourceSelector.isFetching ? 'We are still loading your resources...' : undefined,
      disabled: disabled,
      testId: testId,
      title: 'Resource name'
    }
  }

export const armApplicationDetailToCloudHubApplication = (orgId: string, envId: string, app: ARMApplicationDetail): CloudHubApplication => {
  return {
    ...app,
    organizationId: orgId,
    environmentId: envId,
    id: app.fullDomain,
    region: app.insightsReplayDataRegion,
    mule: app.muleVersion,
  } as CloudHubApplication
}

export const armHybridDetailToHybridApplication = (orgId: string, envId: string, app: ARMHybridDetail): HybridApplication => {
  return {
    ...app,
    organizationId: orgId,
    environmentId: envId,
    id: app.id.toString(),
    details: {serverArtifacts: app.serverArtifacts},
    target: {
      ...app.target,
      id: app.target.id.toString(),
    },
    artifact: {
      lastUpdateTime: app.artifact.timeUpdated
    }
  } as HybridApplication
}

export const rtfApplicationToComponentsRTFApplication = (orgId: string, envId: string, app: RTFApplication): ComponentsRTFApplication => {
  return {
    ...app,
    target: {
      ...app.target,
      id: app.target.targetId
    },
    organizationId: orgId,
    environmentId: envId,
  }
}

export const apiDetailToComponentsAPIDetail = (orgId: string, envId: string, api: APIDetail): ComponentAPIDetail => {
  return {
    ...api,
    organizationId: orgId,
    environmentId: envId,
    id: api.id.toString(),
    apiId: api.apiId || ''
  }
}

export const armServerToComponentsTarget = (orgId: string, envId: string, armServer: ARMServer): MCVTarget => {
  return {
    ...armServer,
    organizationId: orgId,
    environmentId: envId
  }
}

export const transformDetailedResourceToComponentsResource =
  (orgId: string, envId: string, resource: DetailedResource): ComponentsResource | undefined => {
    if (isARMApplicationDetail(resource)) return armApplicationDetailToCloudHubApplication(orgId, envId, resource)
    if (isAPIDetail(resource)) return apiDetailToComponentsAPIDetail(orgId, envId, resource)
    if (isManagedAPI(resource)) return resource
    if (isTarget(resource)) return armServerToComponentsTarget(orgId, envId, resource)
    if (isRTFApplication(resource)) return rtfApplicationToComponentsRTFApplication(orgId, envId, resource)
    if (isARMHybridDetail(resource)) return armHybridDetailToHybridApplication(orgId, envId, resource)
    return undefined
  }

export const resourceNameComparator = (a: Resource, b: Resource) => {
  const aResourceName = getResourceName(a).toUpperCase()
  const bResourceName = getResourceName(b).toUpperCase()
  return aResourceName > bResourceName ? 1 : (aResourceName < bResourceName ? -1 : 0)
}

export const getAppName = (resource: Resource): string => {
  if (isARMApplicationDetail(resource)) return resource.fullDomain
  if (isRTFApplication(resource)) return resource.name || (resource.artifact ? resource.artifact.name : resource.id)
  if (isARMHybridDetail(resource)) return resource.name
  return ''
}
